import React from 'react';
import { Commission, Invoice } from '../../../providers/api/dtos';
import { useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';

export type UseInvoiceCommissionsByRoleDataResult = {
  data?: Commission[],
  authorized?: boolean,
};

export function useInvoiceCommissionsByRole(invoice: Invoice): UseInvoiceCommissionsByRoleDataResult {
  const { userIsAdmin, userIsFinance } = useRBAC();
  const { data: currentAgent } = useCurrentAgentSync();
  const { data: assignment } = useAssignmentById(invoice.assignmentId);
  const isAgentOrManager = useCurrentAgentIsSameAgentOrManager(assignment?.agentId);

  const authorized = React.useMemo(() => {
    if (userIsAdmin || userIsFinance) return true;
    if (currentAgent && isAgentOrManager) return true;
    return false;
  }, [currentAgent, isAgentOrManager, userIsAdmin, userIsFinance]);

  const filteredCommissions = React.useMemo(() => {
    if (userIsAdmin || userIsFinance) {
      return invoice.commissions;
    }

    if (currentAgent && isAgentOrManager) {
      const managedAgentIds = new Set([
        currentAgent.id!,
        ...(currentAgent.canManage?.map((agent) => agent.id!) || []),
      ]);
      return invoice.commissions.filter((commission) => managedAgentIds.has(commission.agentId));
    }
    return [];
  }, [currentAgent, invoice.commissions, isAgentOrManager, userIsAdmin, userIsFinance]);

  return {
    data: authorized ? filteredCommissions : undefined,
    authorized,
  };
}
